import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Form, Button, Modal, Alert, Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import personalleft from '../../../assets/img/personal-left.svg';

export default function ChangePassword() {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [retypePassword, setRetypePassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalOtp, setModalOtp] = useState('');
  const [modalNewPassword, setModalNewPassword] = useState('');
  const [modalConfirmPassword, setModalConfirmPassword] = useState('');
  const [showModalNewPassword, setShowModalNewPassword] = useState(false);
  const [showModalConfirmPassword, setShowModalConfirmPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: otp, 3: password
  const [otpVerified, setOtpVerified] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      valid: password.length >= 8 &&
             hasUpperCase &&
             hasLowerCase &&
             hasNumber &&
             hasSpecialChar,
      message: 'Password must be at least 8 characters and contain uppercase, lowercase, number, and special character'
    };
  };

  const handleSave = async () => {
    setError(''); // Clear previous errors

    if (!currentPassword || !newPassword || !retypePassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== retypePassword) {
      setError('New passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.message);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.changePassword({
        currentPassword,
        newPassword,
      });

      if (response.success) {
        toast.success('Password changed successfully!');

        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setRetypePassword('');

        // Navigate back
        navigate(-1);
      } else {
        setError(response.message || 'Failed to change password');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to change password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Send OTP to email
  const handleSendOTP = async () => {
    if (!modalEmail) {
      setForgotPasswordError('Please enter your email address');
      return;
    }

    if (!modalEmail.includes('@')) {
      setForgotPasswordError('Please enter a valid email address');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    try {
      // Use the working forgot-password endpoint
      const response = await authService.sendPasswordResetOTP({ email: modalEmail });

      if (response.success) {
        setForgotPasswordStep(2); // Move to OTP step
        toast.success('A 4-digit OTP has been sent to your email address. Please check your inbox.');
      } else {
        setForgotPasswordError(response.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setForgotPasswordError(error.message || 'Something went wrong. Please try again later.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Step 2: Verify OTP and move to password reset step
  const handleVerifyOTP = async () => {
    if (!modalOtp) {
      setForgotPasswordError('Please enter the OTP');
      return;
    }

    if (modalOtp.length !== 4) {
      setForgotPasswordError('Please enter a valid 4-digit OTP');
      return;
    }

    // Instead of verifying OTP separately, just move to password reset step
    // The actual OTP verification will happen when resetting the password
    setOtpVerified(true);
    setForgotPasswordStep(3);
    setForgotPasswordError('');
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!modalNewPassword || !modalConfirmPassword) {
      setForgotPasswordError('Please enter both password fields');
      return;
    }

    const passwordValidation = validatePassword(modalNewPassword);
    if (!passwordValidation.valid) {
      setForgotPasswordError(passwordValidation.message);
      return;
    }

    if (modalNewPassword !== modalConfirmPassword) {
      setForgotPasswordError('Passwords do not match');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError('');

    try {
      console.log('Attempting password reset with:', {
        email: modalEmail,
        otp: modalOtp,
        newPassword: modalNewPassword
      });

      // Use the working reset-password endpoint directly (it verifies OTP internally)
      console.log('Calling resetPasswordWithOTP with payload:', {
        email: modalEmail,
        otp: modalOtp,
        newPassword: modalNewPassword ? '[REDACTED]' : null
      });

      const resetResponse = await authService.resetPasswordWithOTP({
        email: modalEmail,
        otp: modalOtp,
        newPassword: modalNewPassword
      });

      console.log('Password reset response:', resetResponse);

      // Log the raw error details if it failed
      if (!resetResponse.success) {
        console.log('Password reset failed with details:', {
          success: resetResponse.success,
          message: resetResponse.message,
          error: resetResponse.error,
          details: resetResponse.details
        });
      }

      if (resetResponse.success) {
        setModalVisible(false);
        toast.success('Your password has been reset successfully. Please login with your new password.');

        // Reset all states
        setModalEmail('');
        setModalOtp('');
        setModalNewPassword('');
        setModalConfirmPassword('');
        setForgotPasswordStep(1);
        setOtpVerified(false);
        setForgotPasswordError('');

        navigate(-1);
      } else {
        setForgotPasswordError(resetResponse.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setForgotPasswordError(error.response?.data?.message || error.message || 'Failed to reset password. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Reset modal when closing
  const handleCloseForgotPasswordModal = () => {
    setModalVisible(false);
    setModalEmail('');
    setModalOtp('');
    setModalNewPassword('');
    setModalConfirmPassword('');
    setForgotPasswordStep(1);
    setOtpVerified(false);
    setForgotPasswordError('');
  };

  return (
    <div className="d-flex" style={{ background: "#fafbfc", minHeight: "100vh" }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div className="">
            <div className="card p-4 personal-ids-card">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="personal-head mb-0">
                  <img
                    src={personalleft}
                    style={{ cursor: "pointer" }}
                    className="me-2"
                    width={28}
                    height={28}
                    onClick={() => navigate(-1)}
                  />
                  Change Password
                </h6>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Change Password Form */}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setError('');
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError('');
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters and include uppercase, lowercase, number, and special character
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Retype New Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showRetypePassword ? "text" : "password"}
                      placeholder="Retype new password"
                      value={retypePassword}
                      onChange={(e) => {
                        setRetypePassword(e.target.value);
                        setError('');
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowRetypePassword(!showRetypePassword)}
                    >
                      {showRetypePassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    variant="link"
                    onClick={() => setModalVisible(true)}
                  >
                    Forgotten your password?
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading || !currentPassword || !newPassword || !retypePassword}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>

        {/* Forgot Password Modal */}
        <Modal show={modalVisible} onHide={handleCloseForgotPasswordModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {forgotPasswordStep === 1 ? 'Forgot Password' :
               forgotPasswordStep === 2 ? 'Verify OTP' : 'Reset Password'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {forgotPasswordError && (
              <Alert variant="danger">{forgotPasswordError}</Alert>
            )}

            {/* Step 1: Email Input */}
            {forgotPasswordStep === 1 && (
              <Form.Group className="mb-3">
                <Form.Label>Enter your registered email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={modalEmail}
                  onChange={(e) => {
                    setModalEmail(e.target.value);
                    setForgotPasswordError('');
                  }}
                />
              </Form.Group>
            )}

            {/* Step 2: OTP Verification */}
            {forgotPasswordStep === 2 && (
              <Form.Group className="mb-3">
                <Form.Label>Enter 4-digit OTP sent to {modalEmail}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter 4-digit OTP"
                  value={modalOtp}
                  onChange={(e) => {
                    setModalOtp(e.target.value.replace(/[^0-9]/g, ''));
                    setForgotPasswordError('');
                  }}
                  maxLength={4}
                />
              </Form.Group>
            )}

            {/* Step 3: New Password */}
            {forgotPasswordStep === 3 && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Enter new password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showModalNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={modalNewPassword}
                      onChange={(e) => {
                        setModalNewPassword(e.target.value);
                        setForgotPasswordError('');
                      }}
                      disabled={!otpVerified}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowModalNewPassword(!showModalNewPassword)}
                    >
                      {showModalNewPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm new password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showModalConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={modalConfirmPassword}
                      onChange={(e) => {
                        setModalConfirmPassword(e.target.value);
                        setForgotPasswordError('');
                      }}
                      disabled={!otpVerified}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowModalConfirmPassword(!showModalConfirmPassword)}
                    >
                      {showModalConfirmPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseForgotPasswordModal}>
              Cancel
            </Button>
            {forgotPasswordStep === 1 && (
              <Button
                variant="primary"
                onClick={handleSendOTP}
                disabled={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send OTP'}
              </Button>
            )}
            {forgotPasswordStep === 2 && (
              <Button
                variant="primary"
                onClick={handleVerifyOTP}
                disabled={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            )}
            {forgotPasswordStep === 3 && (
              <Button
                variant="primary"
                onClick={handleResetPassword}
                disabled={forgotPasswordLoading || !otpVerified}
              >
                {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}